//src/components/admin/Dashboard.js
import React, { useState } from "react";
import { fetchMetrics } from "../../api/admin/adminApi";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaSignOutAlt } from "react-icons/fa";

const DashboardAdmin = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const roundToDecimal = (num, decimals = 2) => {
    if (Number.isInteger(num)) return num; // No rounding needed for integers
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  const handleFetchMetrics = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const data = await fetchMetrics(token, startDate, endDate);
      setMetrics(data);
      setError("");
    } catch (err) {
      setError("Failed to fetch metrics. Please try again.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/secret-portal-entry/admin-login-x29f4Y$-fortress");
  };

  const renderTable = (title, data, columns) => (
    <div className="mb-10 ">
      <h3 className="text-xl font-bold text-green-700 mb-4 bg-green-100 w-fit px-8 py-2 rounded-lg">
        {title}
      </h3>
      {data.length > 0 ? (
        <div className="overflow-x-auto rounded-xl shadow-lg shadow-blue-100/50 border border-blue-100">
          <table className="w-full ">
            <thead className="text-white text-sm font-semibold py-4 px-6 text-left first:rounded-tl-lg last:rounded-tr-lg">
              <tr className="bg-[rgba(108,99,255,0.84)]">
                <th className="text-white text-sm font-bold py-3 px-5 text-left">
                  SL No
                </th>
                {columns.map((col) => (
                  <th
                    key={col}
                    className="text-white text-sm font-semibold py-4 px-6 text-left"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-50">
              {data.map((row, index) => (
                <tr
                  key={index}
                  className={`hover:bg-blue-50/50 transition-colors ${
                    index % 2 === 0 ? "bg-white" : "bg-blue-50/30"
                  }`}
                >
                  <td className="py-4 px-6 text-gray-700 font-medium">
                    {index + 1}
                  </td>
                  {Object.values(row).map((val, idx) => (
                    <td key={idx} className="py-4 px-6 text-gray-600">
                      {val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-blue-50/30 border border-blue-100 rounded-lg p-6 text-center">
          <p className="text-blue-700 font-medium">
            No data found for {title.toLowerCase()} in the selected range.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="dashboard-container w-11/12 mx-auto">
      {/* <div className="flex w-full mt-5 mb-10 justify-between items-center">
        <h1 className="text-4xl font-bold">Admin Dashboard</h1>
        <button
          onClick={handleLogout}
          className="logout-button border-2 border-slate-200 bg-red-500 text-white font-bold p-2 rounded-lg hover:bg-red-600"
        >
          Logout
        </button>
      </div> */}
      <div
        className="w-full bg-white mt-4 px-6 py-5 rounded-lg border border-gray-100 relative overflow-hidden"
        style={{
          boxShadow:
            "1px -1px 10px 0px #B1C2F4 inset, -1px 3px 9px 0px #C8DBED inset",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 tracking-tight drop-shadow-sm">
              Admin Dashboard
            </h1>
            <span className="hidden md:block h-8 w-px bg-gradient-to-b from-transparent via-gray-700 to-transparent"></span>
            <p className="hidden md:block text-gray-500 text-sm font-medium">
              Last login: {new Date().toLocaleString()}
            </p>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center justify-center gap-2 bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium px-4 py-2.5 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95 active:shadow-inner"
          >
            <FaSignOutAlt className="text-lg" />

            <span className="">Sign Out</span>
          </button>
        </div>
      </div>
      {error && <p className="text-center">{error}</p>}

      <div className="my-8 p-4 bg-white rounded-lg border border-gray-200 shadow-sm w-full max-w-2xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
         
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  className="block w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || undefined}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <FaCalendarAlt className="h-5 w-5 text-purple-500" />
                </div>
              </div>
            </div>

            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  className="block w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <FaCalendarAlt className="h-5 w-5 text-purple-500" />
                </div>
              </div>
            </div>
          </div>

          
          <button
            onClick={handleFetchMetrics}
            disabled={!startDate || !endDate}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors w-full sm:w-auto
        ${
          !startDate || !endDate
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-purple-600 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        }`}
          >
            Apply Dates
          </button>
        </div>
      </div>

      {metrics && (
        <div className=" pt-6">
          <p className="text-4xl font-bold text-center uppercase tracking-tighter">
            Your Metrics
          </p>

         
          {renderTable(
            "Users Created",
            metrics.usersCreated.map((user) => ({
              Name: user.fullName,
              Email: user.email,
              "Creation Date": new Date(
                user.creation_date
              ).toLocaleDateString(),
            })),
            ["Name", "Email", "Creation Date"]
          )}

          
          {renderTable(
            "Active Users",
            metrics.activeUsers.map((user) => ({
              Name: user.fullName,
              Email: user.email,
              "Last Active Date": new Date(
                user.last_active_date
              ).toLocaleDateString(),
            })),
            ["Name", "Email", "Last Active Date"]
          )}

         
          {renderTable(
            "Projects Created",
            metrics.projectsCreated.map((project) => ({
              "Project Name": project.project_name,
              "Created By": project.user_id.fullName,
              Email: project.user_id.email,
              "Creation Date": new Date(
                project.created_date
              ).toLocaleDateString(),
            })),
            ["Project Name", "Created By", "Email", "Creation Date"]
          )}

          
          {renderTable(
            "Materials Posted",
            metrics.materialsPosted.map((material) => ({
              "Project Name": material.project_name,
              "Material Name": material.material_name,
              "Posted By": material.user_id.fullName,
              Email: material.user_id.email,
              "Posted Date": new Date(
                material.posted_date
              ).toLocaleDateString(),
              "Summary Prompt Tokens": material.summary_prompt_tokens || 0,
              "Summary Completion Tokens":
                material.summary_completion_tokens || 0,
              "Summary Total Tokens": material.summary_total_tokens || 0,
              "Summary Price":
                roundToDecimal(
                  (material.summary_prompt_tokens * 0.15 * 85) / 1000000 +
                    (material.summary_completion_tokens * 0.6 * 85) / 1000000
                ) || 0,
              "Question Prompt Tokens": material.question_prompt_tokens || 0,
              "Question Completion Tokens":
                material.question_completion_tokens || 0,
              "Question Total Tokens": material.question_total_tokens || 0,
              "Question Price":
                roundToDecimal(
                  (material.question_prompt_tokens * 2.5 * 85) / 1000000 +
                    (material.question_completion_tokens * 10 * 85) / 1000000
                ) || 0,
              "Total Price":
                roundToDecimal(
                  roundToDecimal(
                    (material.summary_prompt_tokens * 0.15 * 85) / 1000000 +
                      (material.summary_completion_tokens * 0.6 * 85) / 1000000
                  ) +
                    roundToDecimal(
                      (material.question_prompt_tokens * 2.5 * 85) / 1000000 +
                        (material.question_completion_tokens * 10 * 85) /
                          1000000
                    )
                ) || 0,
            })),
            [
              "Project Name",
              "Material Name",
              "Posted By",
              "Email",
              "Posted Date",
              "Summary Prompt Tokens",
              "Summary Completion Tokens",
              "Summary Total Tokens",
              "Summary Price",
              "Question Prompt Tokens",
              "Question Completion Tokens",
              "Question Total Tokens",
              "Question Price",
              "Total Price",
            ]
          )}

          
          {renderTable(
            "Questionnaires Created",
            metrics.questionnairesCreated.map((q) => ({
              "Project code": q.project_name,
              "Questionnaire Code": q.question_code,
              "Created By": q.user_id.fullName,
              Email: q.user_id.email,
              "Creation Date": new Date(q.creation_date).toLocaleDateString(),
            })),
            [
              "Project Name",
              "Questionnaire Code",
              "Created By",
              "Email",
              "Creation Date",
            ]
          )}

         
          {metrics.groupedResults && (
            <div className="mb-10 mt-20 w-full space-y-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2 text-center uppercase ">
                Assessment Results
              </h3>
              {Object.entries(metrics.groupedResults).map(
                ([groupKey, assessments]) => (
                  <div
                    key={groupKey}
                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
                  >
               
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-3 border-b border-gray-100">
                      <h4 className="font-semibold text-[rgba(108,99,255,0.84)] text-base">
                        {groupKey}
                      </h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[800px]">
                        <thead className="bg-gradient-to-r bg-[rgba(108,99,255,0.84)]">
                          <tr>
                            <th className="py-3 px-6 text-left text-white font-medium text-sm uppercase tracking-wider">
                              #
                            </th>
                            <th className="py-3 px-6 text-left text-white font-medium text-sm uppercase tracking-wider">
                              Name
                            </th>
                            <th className="py-3 px-6 text-left text-white font-medium text-sm uppercase tracking-wider">
                              Email
                            </th>
                            <th className="py-3 px-6 text-center text-white font-medium text-sm uppercase tracking-wider">
                              Attempt
                            </th>
                            <th className="py-3 px-6 text-left text-white font-medium text-sm uppercase tracking-wider">
                              Date
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {assessments.map((assessment, index) => (
                            <tr
                              key={index}
                              className={`transition-colors hover:bg-indigo-50/50 ${
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                            >
                              <td className="py-4 px-6 text-gray-700 font-medium">
                                {index + 1}
                              </td>
                              <td className="py-4 px-6 text-gray-800 font-medium">
                                {assessment.fullName}
                              </td>
                              <td className="py-4 px-6 text-gray-600">
                                {assessment.email}
                              </td>
                              <td className="py-4 px-6 text-center">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-800 font-semibold">
                                  {assessment.attemptNumber}
                                </span>
                              </td>
                              <td className="py-4 px-6 text-gray-700">
                                {new Date(
                                  assessment.createdAt
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardAdmin;
